import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Logo } from "@/components/Shared";
import { useI18n } from "@/context/I18nContext";
import { GRID_BG, LandingFooter, SUPPORT_EMAIL, useDarkBody } from "@/components/landing/LandingUI";
import { LEGAL, LEGAL_UPDATED } from "@/content/legal";

function LegalSection({ s }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-bold text-white">{s.h}</h2>
      {s.p && <p className="mt-3 text-sm leading-relaxed text-slate-300 md:text-base">{s.p}</p>}
      {s.items && (
        <ul className="mt-3 space-y-2.5">
          {s.items.map((it) => (
            <li key={it} className="flex gap-3 text-sm leading-relaxed text-slate-300 md:text-base">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />{it}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function LegalPage({ kind }) {
  const { lang, setLang } = useI18n();
  const id = lang === "id";
  const doc = LEGAL[lang][kind];
  useDarkBody();
  useEffect(() => { window.scrollTo(0, 0); }, [kind]);

  return (
    <div className="dark min-h-screen bg-slate-950 text-slate-100" data-testid={`legal-${kind}-page`}>
      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5">
          <Link to="/" data-testid="legal-logo-link"><Logo className="text-white" /></Link>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="legal-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-emerald-400">
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">{id ? "Kembali ke beranda" : "Back to home"}</span>
            </Link>
            <button data-testid="lang-toggle" onClick={() => setLang(id ? "en" : "id")}
              className="rounded-lg border border-slate-700 px-2.5 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:border-emerald-400/50 hover:text-white">
              {id ? "EN" : "ID"}
            </button>
          </div>
        </div>
      </header>
      <main className="relative overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-10" style={GRID_BG} />
        <div aria-hidden className="pointer-events-none absolute left-1/2 top-0 h-[360px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-400/10 blur-3xl" />
        <article className="relative z-10 mx-auto max-w-3xl px-5 py-16 lg:py-24">
          <span className="text-sm font-medium uppercase tracking-wider text-emerald-400">Legal</span>
          <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl" data-testid="legal-title">{doc.title}</h1>
          <p className="mt-3 text-sm text-slate-500" data-testid="legal-updated">{id ? "Terakhir diperbarui" : "Last updated"}: {LEGAL_UPDATED[lang]}</p>
          <p className="mt-6 text-base leading-relaxed text-slate-300">{doc.intro}</p>
          {doc.sections.map((s) => <LegalSection key={s.h} s={s} />)}
          <section className="mt-12 rounded-2xl border border-emerald-400/20 bg-emerald-400/5 p-6" data-testid="legal-contact">
            <h2 className="font-display text-xl font-bold text-white">{id ? "Kontak" : "Contact"}</h2>
            <p className="mt-2 text-sm text-slate-300">{id ? "Pertanyaan, permintaan data, atau penghapusan akun bisa dikirim ke:" : "Questions, data requests, or account deletion requests can be sent to:"}</p>
            <p className="mt-3 inline-flex items-center gap-2 font-semibold text-emerald-400"><Mail className="h-4 w-4" /> {SUPPORT_EMAIL}</p>
            <p className="mt-1 text-xs text-slate-500">{id ? "Dikelola oleh FitBersama." : "Operated by FitBersama."}</p>
          </section>
        </article>
      </main>
      <LandingFooter />
    </div>
  );
}
